/**
 * Trainova AI — Security & Administrative Audit Log Page
 *
 * Dedicated compliance, security inspection, and administrative mutation tracking
 * for Super Admins and Organization Administrators.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { admin } from '../../../services/api';
import { AuditLogEntry, Tenant } from '../types';
import { useAuth } from '../../../stores/authStore';
import { usePermissions } from '../hooks/usePermissions';
import { useTenants } from '../hooks/useTenants';
import { showToast, showError } from '../../../utils/alerts';
import { AdminPageHeader } from '../components/AdminPageHeader';
import { useTranslation } from '../../../i18n';
import { Pagination } from '../../../components/ui/Pagination';

export const AuditLogPage: React.FC = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { isPlatformAdmin } = usePermissions();
  const { tenants } = useTenants();

  const [logs, setLogs] = useState<AuditLogEntry[]>([]);
  const [total, setTotal] = useState<number>(0);
  const [page, setPage] = useState<number>(1);
  const [limit, setLimit] = useState<number>(20);
  const [loading, setLoading] = useState<boolean>(true);

  // Filters
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [actionCategory, setActionCategory] = useState<string>('ALL');
  const [statusFilter, setStatusFilter] = useState<string>('ALL');
  const [targetTypeFilter, setTargetTypeFilter] = useState<string>('ALL');
  const [selectedTenantId, setSelectedTenantId] = useState<string>('');
  const [autoRefresh, setAutoRefresh] = useState<boolean>(false);

  // Selected Log for Deep Inspection Drawer / Modal
  const [selectedLog, setSelectedLog] = useState<AuditLogEntry | null>(null);

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const res = await admin.getAuditLogs({
        tenantId: isPlatformAdmin ? (selectedTenantId || undefined) : user?.tenantId,
        action: actionCategory !== 'ALL' ? actionCategory : undefined,
        status: statusFilter !== 'ALL' ? statusFilter : undefined,
        targetType: targetTypeFilter !== 'ALL' ? targetTypeFilter : undefined,
        search: searchTerm.trim() || undefined,
        page,
        limit,
      });

      if (res.success && res.data) {
        setLogs(res.data.logs || []);
        setTotal(res.data.total || 0);
      } else {
        showError('Audit Query Failed', res.error?.message || 'Failed to fetch audit log records.');
      }
    } catch (err: any) {
      showError('Network Error', err.message || 'Unable to retrieve audit logs from server.');
    } finally {
      setLoading(false);
    }
  }, [isPlatformAdmin, selectedTenantId, user?.tenantId, actionCategory, statusFilter, targetTypeFilter, searchTerm, page, limit]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  // Optional auto-refresh polling every 15s if enabled
  useEffect(() => {
    if (!autoRefresh) return;
    const interval = setInterval(() => {
      fetchLogs();
    }, 15000);
    return () => clearInterval(interval);
  }, [autoRefresh, fetchLogs]);

  const handleExportCsv = () => {
    if (logs.length === 0) {
      showToast('No audit logs to export', 'info');
      return;
    }

    const headers = ['ID', 'Timestamp', 'Action', 'Actor Email', 'Actor Role', 'Target Type', 'Target ID', 'Tenant ID', 'Status', 'IP Address', 'User Agent'];
    const rows = logs.map((l) => [
      l.id,
      l.timestamp,
      l.action,
      l.actorEmail || l.actorId,
      l.actorRole || 'SYSTEM',
      l.targetType,
      l.targetId,
      l.tenantId || 'global',
      l.status,
      l.ipAddress || '',
      `"${(l.userAgent || '').replace(/"/g, '""')}"`
    ]);

    const csvContent = [headers.join(','), ...rows.map((r) => r.join(','))].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `trainova-audit-logs-${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    showToast('Audit report exported as CSV', 'success');
  };

  const getActionBadgeColor = (action: string) => {
    if (action.startsWith('scenario:') || action.startsWith('simulation:')) {
      return { bg: '#EFF6FF', text: '#1D4ED8', border: '#BFDBFE' };
    }
    if (action.startsWith('user:')) {
      return { bg: '#FAF5FF', text: '#6B21A8', border: '#E9D5FF' };
    }
    if (action.startsWith('auth:')) {
      return { bg: '#ECFDF5', text: '#047857', border: '#A7F3D0' };
    }
    if (action.startsWith('settings:') || action.startsWith('llm_config:')) {
      return { bg: '#FFFBEB', text: '#B45309', border: '#FDE68A' };
    }
    if (action.startsWith('tenant:')) {
      return { bg: '#FDF2F8', text: '#BE185D', border: '#FBCFE8' };
    }
    return { bg: '#F1F5F9', text: '#334155', border: '#CBD5E1' };
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'SUCCESS':
        return <span style={{ padding: '3px 8px', borderRadius: '6px', fontSize: '11.5px', fontWeight: 700, background: '#ECFDF5', color: '#047857', border: '1px solid #A7F3D0' }}>✓ SUCCESS</span>;
      case 'FAILURE':
        return <span style={{ padding: '3px 8px', borderRadius: '6px', fontSize: '11.5px', fontWeight: 700, background: '#FEF2F2', color: '#B91C1C', border: '1px solid #FECACA' }}>✕ FAILURE</span>;
      case 'DENIED':
        return <span style={{ padding: '3px 8px', borderRadius: '6px', fontSize: '11.5px', fontWeight: 700, background: '#FFFBEB', color: '#B45309', border: '1px solid #FDE68A' }}>⚠️ DENIED</span>;
      default:
        return <span style={{ padding: '3px 8px', borderRadius: '6px', fontSize: '11.5px', fontWeight: 700, background: '#F1F5F9', color: '#475569', border: '1px solid #CBD5E1' }}>{status}</span>;
    }
  };

  const formatRelativeTime = (iso: string) => {
    try {
      const date = new Date(iso);
      const now = new Date();
      const diffSec = Math.floor((now.getTime() - date.getTime()) / 1000);
      if (diffSec < 60) return `${diffSec}s ago`;
      const diffMin = Math.floor(diffSec / 60);
      if (diffMin < 60) return `${diffMin}m ago`;
      const diffHr = Math.floor(diffMin / 60);
      if (diffHr < 24) return `${diffHr}h ago`;
      const diffDays = Math.floor(diffHr / 24);
      return `${diffDays}d ago`;
    } catch {
      return iso;
    }
  };

  return (
    <div className="admin-page" style={{ display: 'grid', gap: '20px' }}>
      <AdminPageHeader
        breadcrumbs={[
          { label: 'Admin', path: '/admin' },
          { label: 'Security & Audit Logs' },
        ]}
        icon="🛡️"
        title="Audit & Compliance Log Explorer"
        badge="Immutable Ledger"
        description="Immutable tracking of administrative operations, security authorizations, role shifts, and scenario blueprints across tenant organizations."
        actions={
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
            <button
              type="button"
              onClick={() => setAutoRefresh(!autoRefresh)}
              className="admin-btn admin-btn--secondary"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
                fontSize: '12.5px',
                background: autoRefresh ? '#ECFDF5' : 'rgba(255, 255, 255, 0.1)',
                borderColor: autoRefresh ? '#10B981' : 'rgba(255, 255, 255, 0.25)',
                color: autoRefresh ? '#047857' : '#FFFFFF',
              }}
            >
              <span style={{ display: 'inline-block', width: '8px', height: '8px', borderRadius: '50%', background: autoRefresh ? '#10B981' : '#94A3B8' }} />
              {autoRefresh ? 'Live Polling ON' : 'Live Polling OFF'}
            </button>

            <button
              type="button"
              onClick={fetchLogs}
              disabled={loading}
              className="admin-btn admin-btn--secondary"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
                fontSize: '12.5px',
                background: 'rgba(255, 255, 255, 0.1)',
                borderColor: 'rgba(255, 255, 255, 0.25)',
                color: '#FFFFFF',
              }}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21.5 2v6h-6M21.34 15.57a10 10 0 1 1-.57-8.38l5.67-5.67"/></svg>
              Refresh
            </button>

            <button
              type="button"
              onClick={handleExportCsv}
              className="admin-btn admin-btn--primary"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
                fontSize: '12.5px',
                background: '#2563eb',
                color: '#ffffff',
                border: 'none',
                fontWeight: 700,
              }}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
              Export CSV
            </button>
          </div>
        }
      />

      {/* Overview Stat Cards */}
      <div className="admin-stats-grid">
        <div className="admin-stat-card">
          <div className="admin-stat-card__icon" style={{ background: '#EFF6FF', color: '#2563EB' }}>📜</div>
          <div className="admin-stat-card__content">
            <span className="admin-stat-card__label">{t('audit.stats.total_events', undefined, 'Total Recorded Events')}</span>
            <span className="admin-stat-card__value">{total}</span>
          </div>
        </div>

        <div className="admin-stat-card">
          <div className="admin-stat-card__icon" style={{ background: '#ECFDF5', color: '#059669' }}>🛡️</div>
          <div className="admin-stat-card__content">
            <span className="admin-stat-card__label">{t('audit.stats.successful_auth', undefined, 'Successful Authorizations')}</span>
            <span className="admin-stat-card__value" style={{ color: '#059669' }}>
              {logs.filter(l => l.status === 'SUCCESS').length}
            </span>
          </div>
        </div>

        <div className="admin-stat-card">
          <div className="admin-stat-card__icon" style={{ background: '#FAF5FF', color: '#7C3AED' }}>👥</div>
          <div className="admin-stat-card__content">
            <span className="admin-stat-card__label">{t('audit.stats.user_mutations', undefined, 'User & Role Mutations')}</span>
            <span className="admin-stat-card__value" style={{ color: '#7C3AED' }}>
              {logs.filter(l => l.action.startsWith('user:') || l.targetType === 'user').length}
            </span>
          </div>
        </div>

        <div className="admin-stat-card">
          <div className="admin-stat-card__icon" style={{ background: '#FFFBEB', color: '#D97706' }}>🎬</div>
          <div className="admin-stat-card__content">
            <span className="admin-stat-card__label">{t('audit.stats.scenario_ops', undefined, 'Scenario Operations')}</span>
            <span className="admin-stat-card__value" style={{ color: '#D97706' }}>
              {logs.filter(l => l.action.startsWith('scenario:') || l.targetType === 'scenario').length}
            </span>
          </div>
        </div>
      </div>

      {/* Search & Filter Toolbar */}
      <div style={{ background: 'var(--white)', border: '1px solid var(--line)', borderRadius: '12px', padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: '14px' }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', alignItems: 'center', justifyContent: 'space-between' }}>
          
          {/* Keyword Search */}
          <div style={{ position: 'relative', flex: '1 1 280px', minWidth: '240px' }}>
            <input
              type="text"
              className="admin-form__input"
              placeholder="Search action, actor email, target ID or IP..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setPage(1);
              }}
              style={{ width: '100%', paddingLeft: '36px' }}
            />
            <svg style={{ position: 'absolute', left: '12px', top: '12px', width: '16px', height: '16px', color: 'var(--faint)' }} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
          </div>

          {/* Super Admin Tenant Switcher */}
          {isPlatformAdmin && (
            <div style={{ minWidth: '180px' }}>
              <select
                className="admin-form__select"
                value={selectedTenantId}
                onChange={(e) => {
                  setSelectedTenantId(e.target.value);
                  setPage(1);
                }}
                style={{ width: '100%' }}
              >
                <option value="">All Organizations (Global)</option>
                {tenants.map((t: Tenant) => (
                  <option key={t.id} value={t.id}>
                    {t.name} ({t.slug})
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Action Category Filter */}
          <div style={{ minWidth: '160px' }}>
            <select
              className="admin-form__select"
              value={actionCategory}
              onChange={(e) => {
                setActionCategory(e.target.value);
                setPage(1);
              }}
              style={{ width: '100%' }}
            >
              <option value="ALL">All Action Categories</option>
              <option value="scenario">Scenarios & Blueprints</option>
              <option value="user">User & Roles</option>
              <option value="auth">Authentication & Login</option>
              <option value="settings">Settings & Policies</option>
              <option value="tenant">Organizations / Tenants</option>
            </select>
          </div>

          {/* Target Type Filter */}
          <div style={{ minWidth: '140px' }}>
            <select
              className="admin-form__select"
              value={targetTypeFilter}
              onChange={(e) => {
                setTargetTypeFilter(e.target.value);
                setPage(1);
              }}
              style={{ width: '100%' }}
            >
              <option value="ALL">All Targets</option>
              <option value="scenario">Scenario</option>
              <option value="user">User</option>
              <option value="tenant">Organization</option>
              <option value="settings">Settings</option>
              <option value="auth">Auth</option>
            </select>
          </div>

          {/* Status Filter */}
          <div style={{ minWidth: '130px' }}>
            <select
              className="admin-form__select"
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setPage(1);
              }}
              style={{ width: '100%' }}
            >
              <option value="ALL">All Statuses</option>
              <option value="SUCCESS">SUCCESS</option>
              <option value="FAILURE">FAILURE</option>
              <option value="DENIED">DENIED</option>
            </select>
          </div>
        </div>
      </div>

      {/* Audit Log Table */}
      <div className="admin-table-container">
        <table className="admin-table">
          <thead>
            <tr>
              <th>Timestamp</th>
              <th>Action</th>
              <th>Actor</th>
              <th>Target</th>
              <th>Status</th>
              <th>Origin / IP</th>
              <th style={{ textAlign: 'right' }}>Payload</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={7} style={{ textAlign: 'center', padding: '40px', color: 'var(--muted)' }}>
                  <div className="dashboard-spinner" style={{ margin: '0 auto 10px' }} />
                  Streaming compliance audit logs...
                </td>
              </tr>
            ) : logs.length === 0 ? (
              <tr>
                <td colSpan={7} style={{ textAlign: 'center', padding: '40px', color: 'var(--muted)' }}>
                  No audit events found matching the specified filters.
                </td>
              </tr>
            ) : (
              logs.map((log) => {
                const badgeStyle = getActionBadgeColor(log.action);
                return (
                  <tr key={log.id}>
                    <td>
                      <div style={{ fontSize: '13px', fontWeight: 600, color: 'var(--ink)' }}>
                        {formatRelativeTime(log.timestamp)}
                      </div>
                      <div style={{ fontSize: '11px', color: 'var(--faint)', fontFamily: 'monospace' }}>
                        {new Date(log.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
                      </div>
                    </td>

                    <td>
                      <span
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '5px',
                          padding: '3px 8px',
                          borderRadius: '6px',
                          fontSize: '12px',
                          fontWeight: 600,
                          backgroundColor: badgeStyle.bg,
                          color: badgeStyle.text,
                          border: `1px solid ${badgeStyle.border}`,
                          fontFamily: 'monospace',
                        }}
                      >
                        {log.action}
                      </span>
                    </td>

                    <td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <div
                          style={{
                            width: '26px',
                            height: '26px',
                            borderRadius: '50%',
                            background: '#E2E8F0',
                            color: '#475569',
                            display: 'grid',
                            placeItems: 'center',
                            fontWeight: 700,
                            fontSize: '10.5px',
                          }}
                        >
                          {(log.actorEmail || log.actorId || 'U').substring(0, 2).toUpperCase()}
                        </div>
                        <div>
                          <div style={{ fontSize: '12.5px', fontWeight: 600, color: 'var(--ink)' }}>
                            {log.actorEmail || log.actorId}
                          </div>
                          {log.actorRole && (
                            <span style={{ fontSize: '10.5px', color: 'var(--muted)', fontWeight: 500 }}>
                              {log.actorRole}
                            </span>
                          )}
                        </div>
                      </div>
                    </td>

                    <td>
                      <div>
                        <span style={{ fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.04em', color: 'var(--faint)', fontWeight: 700 }}>
                          {log.targetType}
                        </span>
                        <div style={{ fontSize: '12px', color: 'var(--ink)', fontFamily: 'monospace', maxWidth: '160px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={log.targetId}>
                          {log.targetId}
                        </div>
                      </div>
                    </td>

                    <td>{getStatusBadge(log.status)}</td>

                    <td>
                      <div style={{ fontSize: '12px', color: 'var(--muted)', fontFamily: 'monospace' }}>
                        {log.ipAddress || '127.0.0.1'}
                      </div>
                    </td>

                    <td style={{ textAlign: 'right' }}>
                      <button
                        type="button"
                        onClick={() => setSelectedLog(log)}
                        className="admin-btn admin-btn--secondary admin-btn--sm"
                        style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}
                      >
                        <span>🔍</span> Inspect
                      </button>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>

        {/* Pagination Toolbar */}
        {total > 0 && (
          <Pagination
            currentPage={page}
            totalItems={total}
            pageSize={limit}
            onPageChange={setPage}
            onPageSizeChange={(newLimit) => {
              setLimit(newLimit);
              setPage(1);
            }}
            showPageSizeSelector
            pageSizeOptions={[10, 20, 50, 100]}
            itemLabel="audit entries"
            disabled={loading}
          />
        )}
      </div>

      {/* JSON Audit Payload Inspector Modal */}
      {selectedLog && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(15, 23, 42, 0.65)',
            backdropFilter: 'blur(4px)',
            display: 'grid',
            placeItems: 'center',
            zIndex: 9999,
            padding: '20px',
          }}
          onClick={() => setSelectedLog(null)}
        >
          <div
            style={{
              backgroundColor: '#FFFFFF',
              borderRadius: '16px',
              maxWidth: '680px',
              width: '100%',
              maxHeight: '85vh',
              display: 'flex',
              flexDirection: 'column',
              boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
              overflow: 'hidden',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div
              style={{
                padding: '18px 24px',
                borderBottom: '1px solid #E2E8F0',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                background: '#F8FAFC',
              }}
            >
              <div>
                <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 700, color: '#1E293B', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span>🔒</span> Audit Event Payload: {selectedLog.action}
                </h3>
                <span style={{ fontSize: '12px', color: '#64748B', fontFamily: 'monospace' }}>
                  {selectedLog.id} • {new Date(selectedLog.timestamp).toLocaleString()}
                </span>
              </div>
              <button
                type="button"
                onClick={() => setSelectedLog(null)}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '18px',
                  color: '#64748B',
                  cursor: 'pointer',
                  fontWeight: 700,
                  padding: '4px 8px',
                  borderRadius: '6px',
                }}
              >
                ✕
              </button>
            </div>

            {/* Modal Content */}
            <div style={{ padding: '20px 24px', overflowY: 'auto', display: 'grid', gap: '16px' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', background: '#F1F5F9', padding: '12px 16px', borderRadius: '10px', fontSize: '12.5px' }}>
                <div>
                  <span style={{ color: '#64748B', display: 'block', fontSize: '11px', fontWeight: 600 }}>ACTOR</span>
                  <strong>{selectedLog.actorEmail || selectedLog.actorId}</strong> ({selectedLog.actorRole || 'SYSTEM'})
                </div>
                <div>
                  <span style={{ color: '#64748B', display: 'block', fontSize: '11px', fontWeight: 600 }}>TARGET</span>
                  <strong>{selectedLog.targetType}:</strong> {selectedLog.targetId}
                </div>
                <div>
                  <span style={{ color: '#64748B', display: 'block', fontSize: '11px', fontWeight: 600 }}>STATUS</span>
                  {getStatusBadge(selectedLog.status)}
                </div>
                <div>
                  <span style={{ color: '#64748B', display: 'block', fontSize: '11px', fontWeight: 600 }}>ORGANIZATION</span>
                  <strong>{selectedLog.tenantId || 'global'}</strong>
                </div>
              </div>

              <div>
                <label style={{ fontSize: '12px', fontWeight: 700, color: '#334155', display: 'block', marginBottom: '6px' }}>
                  Sanitized Event Metadata (JSON):
                </label>
                <pre
                  style={{
                    background: '#0F172A',
                    color: '#38BDF8',
                    padding: '14px 16px',
                    borderRadius: '10px',
                    fontSize: '12.5px',
                    fontFamily: 'monospace',
                    overflowX: 'auto',
                    margin: 0,
                    lineHeight: '1.5',
                  }}
                >
                  {JSON.stringify(selectedLog.details || {}, null, 2)}
                </pre>
              </div>

              {selectedLog.userAgent && (
                <div style={{ fontSize: '11.5px', color: '#64748B', borderTop: '1px solid #E2E8F0', paddingTop: '10px' }}>
                  <strong>Client User-Agent:</strong> {selectedLog.userAgent}
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div style={{ padding: '14px 24px', borderTop: '1px solid #E2E8F0', display: 'flex', justifyContent: 'flex-end', background: '#F8FAFC' }}>
              <button
                type="button"
                onClick={() => setSelectedLog(null)}
                className="admin-btn admin-btn--secondary"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
